import {
  FLEET_BY_KIND,
  GRID_SIZE,
  cellsOf,
  type BattleshipView,
  type Placement,
  type ShipKind,
} from '@bn/shared';

export const COLUMNS = Array.from({ length: GRID_SIZE }, (_, i) =>
  String.fromCharCode(65 + i),
);
export const ROWS = Array.from({ length: GRID_SIZE }, (_, i) => i + 1);

/** Référence lisible d'une case, à la française : colonne puis ligne (ex. « C7 »). */
export function cellLabel(x: number, y: number): string {
  return `${COLUMNS[x] ?? '?'}${y + 1}`;
}

export interface OwnCell {
  ship: ShipKind | null;
  hit: boolean;
  miss: boolean;
  sunk: boolean;
}

function emptyGrid<T>(make: () => T): T[][] {
  return Array.from({ length: GRID_SIZE }, () =>
    Array.from({ length: GRID_SIZE }, make),
  );
}

/** Grille du joueur : sa flotte, les impacts reçus et les tirs manqués adverses. */
export function buildOwnGrid(view: BattleshipView): OwnCell[][] {
  const grid = emptyGrid<OwnCell>(() => ({
    ship: null,
    hit: false,
    miss: false,
    sunk: false,
  }));

  for (const ship of view.you.ships) {
    const sunk = ship.hits.every(Boolean);
    cellsOf(ship, ship.size).forEach((cell, index) => {
      const row = grid[cell.y];
      const target = row?.[cell.x];
      if (!target) return;
      target.ship = ship.kind;
      target.hit = ship.hits[index] === true;
      target.sunk = sunk;
    });
  }

  for (const shot of view.you.incoming) {
    const target = grid[shot.y]?.[shot.x];
    if (target && shot.result === 'miss') target.miss = true;
  }

  return grid;
}

export type TargetState = 'inconnu' | 'rate' | 'touche' | 'coule';

/** Grille adverse : uniquement ce que le joueur a découvert. */
export function buildTargetGrid(view: BattleshipView): TargetState[][] {
  const grid = emptyGrid<TargetState>(() => 'inconnu');

  for (const shot of view.opponent.shots) {
    const row = grid[shot.y];
    if (!row) continue;
    row[shot.x] = shot.result === 'miss' ? 'rate' : 'touche';
  }

  for (const ship of view.opponent.sunk) {
    for (const cell of cellsOf(ship, ship.size)) {
      const row = grid[cell.y];
      if (row) row[cell.x] = 'coule';
    }
  }

  return grid;
}

/** Cases occupées par une flotte en cours de placement. */
export function occupiedCells(placements: Placement[]): Map<string, ShipKind> {
  const map = new Map<string, ShipKind>();
  for (const placement of placements) {
    for (const cell of cellsOf(placement, FLEET_BY_KIND[placement.kind].size)) {
      map.set(`${cell.x},${cell.y}`, placement.kind);
    }
  }
  return map;
}

/** Un navire tient-il à cet endroit sans sortir de la grille ni chevaucher ? */
export function canPlace(
  placements: Placement[],
  candidate: Placement,
): boolean {
  const size = FLEET_BY_KIND[candidate.kind].size;
  const occupied = occupiedCells(placements.filter((p) => p.kind !== candidate.kind));
  return cellsOf(candidate, size).every(
    (cell) =>
      cell.x >= 0 &&
      cell.y >= 0 &&
      cell.x < GRID_SIZE &&
      cell.y < GRID_SIZE &&
      !occupied.has(`${cell.x},${cell.y}`),
  );
}
